import * as React from "react";
import logo from "@/assets/EcoPROFIT_Wordmark.png";
import smallLogo from "@/assets/LCT_CircleIcon.png";

import { Sidebar, SidebarContent, SidebarHeader, useSidebar } from "@/components/ui/sidebar";
import { CogIcon, FilePenLine, House, Link as LinkIcon, ListChecks, LogOut, Mail, Plug, UsersRound } from "lucide-react";
import { Link, useLocation } from "@tanstack/react-router";
import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useAuth } from "@/auth/hooks/useAuth";
import { useAppSelector } from "@/hooks/reduxHooks";
import { Tooltip } from "./Tooltip";

export const AppSidebar = ({ ...props }: React.ComponentProps<typeof Sidebar>) => {
  const { pathname } = useLocation();
  const isStaff = useAppSelector((state) => state.user.isStaff);
  const { tryLogout } = useAuth();

  type NavLink = {
    title: string;
    icon: React.ElementType;
    to?: string;
    onClick?: () => void;
  };

  const navLinks: NavLink[] = [
    {
      title: "Dashboard",
      icon: House,
      to: "/dashboard",
    },
    {
      title: "Certifications",
      icon: FilePenLine,
      to: "/pvp",
    },
    {
      title: "Interventions",
      icon: ListChecks,
      to: "/interventions",
    },
    {
      title: "Connected Systems",
      icon: Plug,
      to: "/connected-systems",
    },
    {
      title: "Supply Chain",
      icon: LinkIcon,
      to: "/supply-chain",
    },
    {
      title: "Messages",
      icon: Mail,
      to: "/messages",
    },
  ];

  const adminNavLinks: NavLink[] = [
    {
      title: "Certification Applications",
      icon: FilePenLine,
      to: "/admin/pvp",
    },
    {
      title: "Users",
      icon: UsersRound,
      to: "/admin/users",
    },
  ];

  const bottomNavLinks: NavLink[] = [
    {
      title: "Settings",
      icon: CogIcon,
      to: "/settings",
    },
    {
      title: "Logout",
      icon: LogOut,
      onClick: () => {
        tryLogout();
      },
    },
  ];

  const mainLinks = isStaff ? adminNavLinks : navLinks;

  const { state, isMobile, setOpenMobile } = useSidebar();
  const collapsed = state === "collapsed" && !isMobile;

  const renderLink = (link: NavLink) => {
    const isParentPath = link.to && pathname.startsWith(link.to);
    const isExactPath = link.to === pathname;

    const handleClick = () => {
      // Close mobile sidebar when a menu item is clicked
      if (isMobile) {
        setOpenMobile(false);
      }
      // Call the original onClick if it exists
      link?.onClick?.();
    };

    const linkContent = (
      <Link
        key={link.title}
        to={link.to ?? "#"}
        onClick={handleClick}
        className={cn(
          buttonVariants({ variant: "ghost", size: collapsed ? "icon" : "sm" }),
          "justify-start text-wrap text-sm text-slate-900 hover:bg-white",
          isParentPath && "hover:bg-primary-accent bg-primaryAccent text-sm text-slate-800",
          !isExactPath && "hover:pointer-events-auto",
          "py-7",
          collapsed && "!justify-center !p-0",
        )}
      >
        <link.icon className={cn("m-2 h-4 w-4")} />
        {!collapsed && <span>{link.title}</span>}
      </Link>
    );

    if (collapsed) {
      return (
        <Tooltip label={link.title} side="right">
          {linkContent}
        </Tooltip>
      );
    }

    return linkContent;
  };

  return (
    <Sidebar {...props} className={cn("bg-white p-4", collapsed && "!p-1")} collapsible={isMobile ? "offcanvas" : "icon"}>
      <SidebarHeader className="flex items-center justify-center bg-white">
        <img className={cn("min-h-12 p-2", collapsed ? "min-w-12" : "min-w-56")} src={!collapsed ? logo : smallLogo} alt="logo" />
      </SidebarHeader>
      <SidebarContent className="flex flex-col justify-between bg-white pt-4">
        <div className="flex flex-col">{mainLinks.map((link) => renderLink(link))}</div>
        <div className="flex flex-col">{bottomNavLinks.map((link) => renderLink(link))}</div>
      </SidebarContent>
    </Sidebar>
  );
};
